'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const students = [
    { name: 'Ama Owusu', grade: 'Grade 12', avg: 94, sessions: 18, status: 'active', initials: 'AO' },
    { name: 'Kwame Asante', grade: 'Grade 10', avg: 78, sessions: 12, status: 'active', initials: 'KA' },
    { name: 'Efua Mensah', grade: 'Grade 11', avg: 88, sessions: 15, status: 'active', initials: 'EM' },
    { name: 'Kofi Boateng', grade: 'Grade 9', avg: 65, sessions: 7, status: 'at-risk', initials: 'KB' },
    { name: 'Abena Darko', grade: 'Grade 12', avg: 91, sessions: 20, status: 'active', initials: 'AD' },
];

const assessments = [
    { title: 'Calculus Mid-Term', subject: 'Mathematics', due: 'Mar 15', submissions: 16, total: 18, graded: 12 },
    { title: 'Essay: Climate Change', subject: 'English', due: 'Mar 18', submissions: 22, total: 24, graded: 22 },
    { title: 'Physics Lab Report', subject: 'Physics', due: 'Mar 20', submissions: 8, total: 22, graded: 0 },
];

export default function StudioStudents() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="students" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#F5F6FF' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 20% 50%, rgba(55,87,255,0.06) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                        style={{ background: 'rgba(55,87,255,0.1)', color: BRAND, border: '1px solid rgba(55,87,255,0.2)', letterSpacing: '0.15em' }}
                    >
                        Students & Assessments
                    </div>
                    <div className="grid lg:grid-cols-2 gap-8 items-end">
                        <h2
                            className="font-display font-800"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Know every student.
                            <br />
                            <span style={{ color: BRAND }}>Grade every moment.</span>
                        </h2>
                        <p className="text-base leading-relaxed" style={{ color: '#666', maxWidth: '420px' }}>
                            Manage your full student roster, track individual performance, create assessments, and review submissions — all in one workspace.
                        </p>
                    </div>
                </div>

                <div className={`grid lg:grid-cols-5 gap-5 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {/* Student roster — spans 3 cols */}
                    <div className="lg:col-span-3 rounded-3xl p-7" style={{ background: 'white', border: '1px solid rgba(55,87,255,0.1)' }}>
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-sm font-800" style={{ color: '#0D0D0D' }}>Student Roster</p>
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ background: 'rgba(55,87,255,0.06)', border: '1px solid rgba(55,87,255,0.1)' }}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <circle cx="5" cy="5" r="3.5" stroke={BRAND} strokeWidth="1.25" />
                                    <path d="M8.5 8.5l2 2" stroke={BRAND} strokeWidth="1.25" strokeLinecap="round" />
                                </svg>
                                <span className="text-xs font-600" style={{ color: BRAND }}>Search students</span>
                            </div>
                        </div>
                        <div className="flex flex-col gap-3">
                            {students.map((student, i) => (
                                <div
                                    key={student.name}
                                    className="flex items-center gap-4 p-3 rounded-2xl transition-all duration-200 cursor-pointer"
                                    style={{ transitionDelay: `${i * 50}ms` }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(55,87,255,0.04)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                >
                                    <div
                                        className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-800 flex-shrink-0"
                                        style={{ background: student.status === 'at-risk' ? 'rgba(239,68,68,0.1)' : 'rgba(55,87,255,0.1)', color: student.status === 'at-risk' ? '#EF4444' : BRAND }}
                                    >
                                        {student.initials}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-700 truncate" style={{ color: '#0D0D0D' }}>{student.name}</p>
                                        <p className="text-xs font-500" style={{ color: '#888' }}>{student.grade} · {student.sessions} sessions</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="text-sm font-800" style={{ color: student.avg >= 80 ? BRAND : student.avg >= 70 ? '#F59E0B' : '#EF4444' }}>{student.avg}%</p>
                                        <span
                                            className="text-xs font-600 px-2 py-0.5 rounded-full"
                                            style={{
                                                background: student.status === 'at-risk' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                                                color: student.status === 'at-risk' ? '#EF4444' : '#10B981',
                                            }}
                                        >
                                            {student.status === 'at-risk' ? 'At risk' : 'Active'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Assessments — spans 2 cols */}
                    <div className="lg:col-span-2 flex flex-col gap-5">
                        {/* Assessment builder promo */}
                        <div className="rounded-3xl p-7" style={{ background: BRAND }}>
                            <p className="text-xs font-700 uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.12em' }}>Assessment Builder</p>
                            <p className="text-base font-800 text-white mb-2">Create tests, quizzes, and assignments</p>
                            <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.7)' }}>Automated grading, rubrics, and detailed result analytics included.</p>
                            <div
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-700 cursor-pointer transition-all duration-200"
                                style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.25)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.15)'; }}
                            >
                                New assessment
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        {/* Active assessments */}
                        <div className="rounded-3xl p-7 flex-1" style={{ background: 'white', border: '1px solid rgba(55,87,255,0.1)' }}>
                            <p className="text-sm font-800 mb-5" style={{ color: '#0D0D0D' }}>Active Assessments</p>
                            <div className="flex flex-col gap-4">
                                {assessments.map((a) => (
                                    <div key={a.title} className="pb-4" style={{ borderBottom: '1px solid rgba(55,87,255,0.06)' }}>
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <p className="text-xs font-800" style={{ color: '#0D0D0D' }}>{a.title}</p>
                                                <p className="text-xs font-500" style={{ color: '#888' }}>{a.subject} · Due {a.due}</p>
                                            </div>
                                            <span className="text-xs font-700 px-2 py-0.5 rounded-full flex-shrink-0 ml-2" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>
                                                {a.graded}/{a.submissions}
                                            </span>
                                        </div>
                                        <div className="w-full h-1.5 rounded-full" style={{ background: 'rgba(55,87,255,0.08)' }}>
                                            <div
                                                className="h-1.5 rounded-full transition-all duration-1000"
                                                style={{ width: visible ? `${(a.submissions / a.total) * 100}%` : '0%', background: BRAND }}
                                            />
                                        </div>
                                        <p className="text-xs font-500 mt-1" style={{ color: '#AAA' }}>{a.submissions}/{a.total} submitted</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
